import React, { useEffect, useState, useCallback } from 'react';
import useIsMobile from '../hooks/useIsMobile';

function fmtMoney(v, code = 'INR') {
  const n = Number(v || 0);
  try {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: code, maximumFractionDigits: 0 }).format(n);
  } catch {
    return `${code} ${n.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
  }
}

function fmtShortDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

const OVERLAP_LABEL = {
  SAME_DAY: '',
  MULTI_DAY_FULL_OVERLAP: '📅 Multi-day',
  MULTI_DAY_PARTIAL_OVERLAP: '⚠ Partial overlap',
  DAY_AGAINST_MULTI_DAY: '⚠ Day vs multi-day',
};
const OVERLAP_BG = {
  SAME_DAY: 'transparent',
  MULTI_DAY_FULL_OVERLAP: '#cffafe',
  MULTI_DAY_PARTIAL_OVERLAP: '#fef3c7',
  DAY_AGAINST_MULTI_DAY: '#fef3c7',
};

function PmsBanquetLeadConflicts({ token, selectedProperty, focusClusterKey, onClearFocus }) {
  const { isMobile } = useIsMobile();
  const [clusters, setClusters] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [overrideModal, setOverrideModal] = useState(null);
  const [overrideReason, setOverrideReason] = useState('');
  const [overrideBusy, setOverrideBusy] = useState(false);
  // Pulse for ~2.5s when we land here with a focusClusterKey so the user
  // sees which cluster matches the badge they clicked.
  const [pulseKey, setPulseKey] = useState(null);
  const currency = selectedProperty?.currency_code || 'INR';

  const authHeaders = useCallback(() => ({
    Authorization: `Bearer ${token}`,
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || '',
    'Content-Type': 'application/json',
  }), [token, selectedProperty]);

  const load = useCallback(async () => {
    if (!selectedProperty?.id) return;
    setLoading(true);
    setError('');
    try {
      const r = await fetch('/api/v1/banquet-crm/lead-arbitration/clusters?status=OPEN', { headers: authHeaders() });
      const body = await r.json();
      if (!r.ok) throw new Error(body?.error || 'Failed to load');
      setClusters(body.clusters || []);
      setWarnings(body.warnings || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [selectedProperty, authHeaders]);

  useEffect(() => { load(); }, [load]);

  // When the leads list deep-links us to a specific cluster, scroll the
  // matching card into view and pulse its border for visual confirmation.
  // Then clear the focus so a manual refresh later doesn't re-scroll.
  useEffect(() => {
    if (!focusClusterKey || clusters.length === 0) return;
    const target = clusters.find(c => c.cluster_key === focusClusterKey);
    if (!target) return;
    setPulseKey(focusClusterKey);
    const el = document.getElementById(`bla-cluster-${focusClusterKey}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    const t = setTimeout(() => {
      setPulseKey(null);
      if (typeof onClearFocus === 'function') onClearFocus();
    }, 2500);
    return () => clearTimeout(t);
  }, [focusClusterKey, clusters, onClearFocus]);

  const recompute = async (clusterKey) => {
    try {
      const r = await fetch(`/api/v1/banquet-crm/lead-arbitration/clusters/${clusterKey}/recompute`,
        { method: 'POST', headers: authHeaders() });
      const body = await r.json();
      if (!r.ok) throw new Error(body?.error || 'Failed');
      load();
    } catch (e) { setError(e.message); }
  };

  const dismiss = async (clusterKey) => {
    if (!window.confirm('Dismiss this cluster? Members keep their flags for history.')) return;
    try {
      const r = await fetch(`/api/v1/banquet-crm/lead-arbitration/clusters/${clusterKey}/resolve`,
        { method: 'POST', headers: authHeaders(), body: JSON.stringify({ reason: 'MANUAL_DISMISS' }) });
      const body = await r.json();
      if (!r.ok) throw new Error(body?.error || 'Failed');
      load();
    } catch (e) { setError(e.message); }
  };

  const submitOverride = async () => {
    if (!overrideModal || !overrideReason.trim()) return;
    setOverrideBusy(true);
    try {
      const r = await fetch(`/api/v1/banquet-crm/lead-arbitration/clusters/${overrideModal.cluster_key}/override`,
        { method: 'POST', headers: authHeaders(), body: JSON.stringify({ lead_id: overrideModal.lead_id, reason: overrideReason.trim() }) });
      const body = await r.json();
      if (!r.ok) throw new Error(body?.error || 'Failed');
      setOverrideModal(null);
      setOverrideReason('');
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setOverrideBusy(false);
    }
  };

  if (!selectedProperty?.id) {
    return <div style={{ padding: 20 }}><div className="error-banner">Please select a property first.</div></div>;
  }

  return (
    <div style={{ padding: '0.75rem 1rem', fontFamily: 'inherit' }}>
      <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>
        Banquet <span style={{ color: '#cbd5e0' }}>›</span> CRM Leads <span style={{ color: '#cbd5e0' }}>›</span>{' '}
        <span style={{ color: 'var(--theme-primary)', fontWeight: 600 }}>Conflicts</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h3 style={{ margin: 0, color: 'var(--theme-primary)' }}>
          Lead Arbitration — Active Conflicts {clusters.length > 0 && <span style={{ fontSize: 13, color: '#6b7280', fontWeight: 400 }}>({clusters.length})</span>}
        </h3>
        <button onClick={load} disabled={loading}
          style={{ padding: '6px 14px', background: '#2563eb', color: '#fff', border: 'none',
            borderRadius: 6, fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {error && <div className="error-banner" style={{ marginBottom: 12 }}>{error}</div>}

      {warnings.map((w, i) => (
        <div key={i} style={{ padding: '12px 16px', background: '#fffbeb',
          border: '1px solid #fbbf24', borderRadius: 8, color: '#92400e',
          fontSize: 13, marginBottom: 12 }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>⚠ {w.code.replace(/_/g, ' ')}</div>
          <div>{w.message}</div>
          {Array.isArray(w.detail?.overlapping_dates) && w.detail.overlapping_dates.length > 0 && (
            <div style={{ marginTop: 6, fontSize: 12 }}>
              Dates with multiple active leads currently:&nbsp;
              {w.detail.overlapping_dates.map(d => `${d.d} (${d.n} leads)`).join(', ')}
            </div>
          )}
        </div>
      ))}

      {!loading && clusters.length === 0 && warnings.length === 0 && (
        <div style={{ padding: 24, background: '#f0fdf4', border: '1px solid #bbf7d0',
          borderRadius: 8, color: '#166534', fontSize: 13 }}>
          No open conflicts — every active lead has a clear path to its hall on its date.
        </div>
      )}

      {clusters.map((c) => {
        // AI rank-shift detection — when at least one member's AI rank
        // differs from its pure-revenue rank, surface a cluster-level banner.
        const aiShifted = c.ai_enabled && (c.members || []).some(
          m => m.rank_pure_revenue && m.rank_pure_revenue !== m.rank_in_cluster
        );
        const aiPickName = (c.members || []).find(m => m.is_recommended)?.client_name;
        const isPulsing = pulseKey === c.cluster_key;
        return (
        <div key={c.cluster_id}
          id={`bla-cluster-${c.cluster_key}`}
          style={{ background: '#fff',
            border: isPulsing ? '2px solid #2563eb' : '1px solid #e5e7eb',
            borderRadius: 10, padding: 14, marginBottom: 14,
            boxShadow: isPulsing ? '0 0 0 4px rgba(37, 99, 235, 0.2)' : '0 1px 2px rgba(15,23,42,0.04)',
            transition: 'box-shadow 400ms, border-color 400ms' }}>
          {/* Cluster header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
            flexWrap: 'wrap', gap: 8, borderBottom: '1px dashed #e5e7eb', paddingBottom: 8, marginBottom: 10 }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15, color: '#0f172a' }}>
                {c.hall_name || `Hall #${c.hall_id}`}
                <span style={{ marginLeft: 10, fontSize: 12, color: '#475569', fontWeight: 400 }}>
                  · capacity {c.max_capacity || '—'} pax
                </span>
              </div>
              <div style={{ fontSize: 13, color: '#475569', marginTop: 2 }}>
                {fmtShortDate(c.event_from_date)}
                {c.event_from_date !== c.event_to_date ? ` – ${fmtShortDate(c.event_to_date)}` : ''}
                {' · '}
                <b>{c.member_count}</b> competing leads
                {' · '}
                AI {c.ai_enabled ? 'on' : 'off'}
              </div>
              {OVERLAP_LABEL[c.overlap_type] && (
                <div style={{ marginTop: 6, display: 'inline-block',
                  background: OVERLAP_BG[c.overlap_type], color: '#0f172a',
                  padding: '2px 10px', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>
                  {OVERLAP_LABEL[c.overlap_type]}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button onClick={() => recompute(c.cluster_key)}
                style={btn('#fff', '#475569', '#d1d5db')}>↻ Recompute</button>
              <button onClick={() => dismiss(c.cluster_key)}
                style={btn('#fff', '#b91c1c', '#fca5a5')}>Dismiss</button>
            </div>
          </div>

          {/* AI rank-shift banner (decision §13.4) */}
          {aiShifted && (
            <div style={{ background: '#fef3c7', border: '1px solid #fcd34d',
              color: '#92400e', borderRadius: 6, padding: '8px 12px',
              marginBottom: 10, fontSize: 12 }}>
              🧠 <b>AI adjusted the ranking.</b> {aiPickName ? `Pick: ${aiPickName}.` : ''}
              {' '}Soft-factor signals (repeat client, qualification, verification risk) moved a lead's position vs pure revenue. Hover any badge for the percentage.
            </div>
          )}

          {/* Members grid */}
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'minmax(0, 1fr)' : `repeat(${Math.min(c.members.length, 3)}, 1fr)`, gap: 10 }}>
            {c.members.map((m) => {
              const isWinner = m.is_recommended;
              const isOverride = m.is_overridden;
              const accent = isWinner ? (isOverride ? '#7c3aed' : '#16a34a') : '#cbd5e0';
              const badge = isWinner
                ? (isOverride ? '🛡 OVERRIDE PICK' : '⭐ RECOMMENDED')
                : `#${m.rank_in_cluster} of ${c.member_count}`;
              return (
                <div key={m.lead_id}
                  style={{
                    border: `2px solid ${accent}`,
                    borderRadius: 8, padding: 12,
                    background: isWinner ? '#f0fdf4' : '#fff',
                    position: 'relative',
                  }}>
                  <div style={{ position: 'absolute', top: -10, left: 8,
                    background: accent, color: '#fff', padding: '2px 10px',
                    borderRadius: 4, fontSize: 10, fontWeight: 700 }}>
                    {badge}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{m.client_name}</div>
                      <div style={{ fontSize: 11, color: '#64748b' }}>
                        {m.lead_no} · {m.event_type || '—'} · {m.pipeline_stage}
                      </div>
                    </div>
                    {m.membership_type === 'FEASIBLE' && (
                      <span style={{ background: '#dbeafe', color: '#1e40af',
                        padding: '2px 6px', borderRadius: 3, fontSize: 9, fontWeight: 700 }}>
                        FEASIBLE
                      </span>
                    )}
                  </div>
                  <div style={{ marginTop: 8, fontSize: 22, fontWeight: 800, color: '#0f172a',
                    display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                    {fmtMoney(m.projected_total_revenue, currency)}
                    {Number(m.ai_soft_factor_pct) !== 0 && m.ai_soft_factor_pct !== null && (
                      <span style={{
                        fontSize: 11, fontWeight: 700,
                        background: Number(m.ai_soft_factor_pct) > 0 ? '#dcfce7' : '#fee2e2',
                        color:      Number(m.ai_soft_factor_pct) > 0 ? '#166534' : '#991b1b',
                        padding: '2px 8px', borderRadius: 10,
                      }}
                      title={`AI applied a ${m.ai_soft_factor_pct > 0 ? '+' : ''}${m.ai_soft_factor_pct}% soft-factor adjustment. Cap is ±15%.`}>
                        AI {Number(m.ai_soft_factor_pct) > 0 ? '+' : ''}{Number(m.ai_soft_factor_pct).toFixed(1)}%
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: '#64748b' }}>
                    {m.revenue_source === 'QUOTATION' ? 'From quotation' :
                     m.revenue_source === 'LEAD_BUDGET' ? (
                       <span>From budget <span style={{ background: '#e5e7eb', color: '#475569',
                         padding: '1px 6px', borderRadius: 3, fontSize: 9, fontWeight: 700, marginLeft: 4 }}
                         title="Rank based on the client's stated budget. Prepare a quotation to refine.">
                         BUDGET-ONLY
                       </span></span>
                     ) : 'Package fallback'}
                    {m.rank_pure_revenue && m.rank_pure_revenue !== m.rank_in_cluster && (
                      <span style={{ marginLeft: 8, fontSize: 10, color: '#92400e' }}
                        title={`Pure-revenue rank was #${m.rank_pure_revenue}; AI moved it to #${m.rank_in_cluster}.`}>
                        (was #{m.rank_pure_revenue} pre-AI)
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6,
                    fontSize: 11, color: '#475569', marginTop: 8 }}>
                    <div>👥 {m.expected_pax} pax</div>
                    <div>📅 {m.event_total_days}-day event</div>
                    {m.overlap_days_with_cluster !== m.event_total_days && (
                      <div style={{ gridColumn: 'span 2', color: '#92400e' }}>
                        Overlap: {m.overlap_days_with_cluster} day(s) of {m.event_total_days}
                      </div>
                    )}
                    <div>Budget: {fmtMoney(m.budget_min, currency)} – {fmtMoney(m.budget_max, currency)}</div>
                  </div>
                  {m.ai_rationale && (
                    <div style={{ marginTop: 8, padding: 8, background: '#f8fafc',
                      borderRadius: 6, fontSize: 11, color: '#0f172a', borderLeft: '3px solid #2563eb' }}>
                      💡 {m.ai_rationale}
                    </div>
                  )}
                  <div style={{ marginTop: 10, display: 'flex', gap: 6 }}>
                    {!isWinner && (
                      <button onClick={() => { setOverrideModal({ cluster_key: c.cluster_key, lead_id: m.lead_id, lead_no: m.lead_no, client_name: m.client_name }); setOverrideReason(''); }}
                        style={btn('#fff', '#7c3aed', '#c4b5fd', '11px')}>Override → Pick</button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        );
      })}

      {/* Override Modal */}
      {overrideModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}
          onClick={() => setOverrideModal(null)}>
          <div onClick={(e) => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 10, padding: 20, width: 'min(440px, 92vw)' }}>
            <h4 style={{ margin: '0 0 12px', color: '#7c3aed' }}>Override Recommendation</h4>
            <div style={{ fontSize: 13, marginBottom: 12 }}>
              Promote <b>{overrideModal.client_name}</b> ({overrideModal.lead_no}) over the AI's recommendation.
              The audit log will capture your reason.
            </div>
            <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Reason (required)</label>
            <textarea value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)}
              rows={3}
              placeholder="e.g. Long-standing corporate client, prefer relationship over revenue"
              style={{ width: '100%', padding: 8, border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }} />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, marginTop: 12 }}>
              <button onClick={() => setOverrideModal(null)} style={btn('#fff', '#475569', '#d1d5db')}>Cancel</button>
              <button onClick={submitOverride} disabled={!overrideReason.trim() || overrideBusy}
                style={btn('#7c3aed', '#fff', '#7c3aed')}>
                {overrideBusy ? 'Saving…' : 'Confirm Override'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const btn = (bg, color, border, fontSize = '12px') => ({
  padding: '5px 11px',
  background: bg, color, border: `1px solid ${border}`,
  borderRadius: 5, fontSize, fontWeight: 600, cursor: 'pointer',
});

export default PmsBanquetLeadConflicts;
